import { isHttpErrorResponse } from "@utils";

/**
 * Why a refresh token request failed.
 *
 * The auth server only allows a refresh token to be used once, so a failure
 * that is not the server rejecting the grant must not be treated as the end of
 * the session. Signing the user out on a network error throws away a refresh
 * token that is still perfectly usable.
 *
 * - `session-ended`: the server gave a definitive answer that the grant is no
 *   longer valid, for example `400 invalid_grant` because the refresh token
 *   was already used. The user has to sign in again.
 * - `retryable`: no response was received at all, so the request can safely be
 *   sent again with the same refresh token.
 * - `transient`: the server responded, but not about the grant. The refresh
 *   token may or may not have been used, so do not sign the user out and do
 *   not immediately send the same token again.
 */
export type RefreshFailure = "session-ended" | "retryable" | "transient";

/**
 * Status codes where the server answered but told us nothing about the grant.
 */
const transientStatuses = new Set([408, 429]);

/**
 * Names of the errors thrown when waiting for the refresh lock is given up on.
 */
const abortErrorNames = new Set(["AbortError", "TimeoutError"]);

/**
 * True when waiting for the refresh lock was given up on rather than the token
 * request itself failing.
 */
export const isAbortError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return abortErrorNames.has(error.name);
};

export const classifyRefreshFailure = (error: unknown): RefreshFailure => {
  // Something threw before the request produced a response: the lock wait was
  // abandoned, or our own code failed. Neither ends the session, and neither is
  // worth resending the token for. Retrying a defect in our own code would only
  // hide it behind a request storm while leaving the user in a broken app, and
  // an abandoned lock wait is already handled by re-checking for a rotation.
  if (!isHttpErrorResponse(error)) return "transient";

  // Angular reports a request that never got a response as status 0, so the
  // same refresh token can safely be sent again.
  if (error.status === 0) return "retryable";

  if (transientStatuses.has(error.status)) return "transient";
  if (error.status >= 500) return "transient";

  // Any other response is the server definitively refusing the grant.
  return "session-ended";
};

/**
 * Reads the OAuth `error` field from a failed token request, for logging only.
 * The sign-out decision uses `classifyRefreshFailure` instead, so that an
 * unfamiliar error body can never be mistaken for a spent refresh token.
 */
export const getOAuthErrorCode = (error: unknown): string | null => {
  if (!isHttpErrorResponse(error)) return null;

  const body: unknown = error.error;

  if (typeof body === "string") {
    try {
      const parsed: unknown = JSON.parse(body);
      return getErrorCodeFromBody(parsed);
    } catch {
      return null;
    }
  }

  return getErrorCodeFromBody(body);
};

const getErrorCodeFromBody = (body: unknown): string | null => {
  if (typeof body !== "object" || body === null) return null;
  const code: unknown = (body as Record<string, unknown>)["error"];
  return typeof code === "string" ? code : null;
};
